// === Profile Logic ===

import store, { PROFILE_KEY, SEASONS_KEY, DEFAULT_PROFILE, DEFAULT_SEASONS } from '../state/store.js';
import { getItem, setItem } from './storage-service.js';
import { getElements } from '../utils/dom.js';

const SEASON_DEFAULTS = {
  Dairy: { calving: 2, turnout: 4, housing: 10, breeding: 5, dryingOff: 11 },
  Beef:  { calving: 2, turnout: 4, housing: 11, breeding: 5, dryingOff: null },
};

const MONTH_NAMES = [
  '', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

export function loadProfile() {
  const parsed = getItem(PROFILE_KEY);
  if (parsed) {
    store.farmProfile = { ...DEFAULT_PROFILE, ...parsed };
    // Migration: strip legacy calving field
    delete store.farmProfile.calving;
    store.pendingProfile = { ...store.farmProfile };
  } else {
    store.farmProfile = { ...DEFAULT_PROFILE };
    store.pendingProfile = { ...DEFAULT_PROFILE };
  }

  const seasons = getItem(SEASONS_KEY);
  if (seasons) {
    store.farmSeasons = { ...DEFAULT_SEASONS, ...seasons };
    store.pendingSeasons = { ...store.farmSeasons };
  } else {
    store.farmSeasons = { ...DEFAULT_SEASONS };
    store.pendingSeasons = { ...DEFAULT_SEASONS };
  }
}

export function getProfileLabel() {
  const herd = store.farmProfile.herdType || 'Select your herd';
  if (!store.farmSeasons.calving) return herd;
  const cal = MONTH_NAMES[store.farmSeasons.calving] || '';
  return `${herd} · Calving: ${cal}`;
}

// Reset the seasonal dates to the sensible defaults for the current herd type
// (roughly fits most Irish herds). Persists and keeps pendingSeasons in sync.
export function applyDefaultSeasons() {
  const herd = store.farmProfile.herdType;
  const defaults = SEASON_DEFAULTS[herd] || SEASON_DEFAULTS.Dairy;
  store.farmSeasons = { ...DEFAULT_SEASONS, ...defaults };
  store.pendingSeasons = { ...store.farmSeasons };
  setItem(SEASONS_KEY, store.farmSeasons);
}

export function toggleOnboarding(show, step = 1) {
  const els = getElements();
  els.onboarding.style.display = show ? 'flex' : 'none';
  document.body.classList.toggle('onboarding-active', show);
  document.body.classList.toggle('onboarding-step-1', show && step === 1);
  document.body.classList.toggle('onboarding-step-2', show && step === 2);
  if (show) {
    els.onboardStep1.style.display = step === 1 ? 'flex' : 'none';
    els.onboardStep2.style.display = step === 2 ? 'flex' : 'none';
  }
  if (!show) {
    els.onboardError.style.display = 'none';
  }
}

export function setPendingChoice(key, value) {
  const els = getElements();
  store.pendingProfile[key] = value;
  updateChoiceButtons(els.herdButtons, value);
  showSeasonDates(value);
}

function showSeasonDates(herdType) {
  const els = getElements();
  if (!herdType) {
    els.seasonDatesSection.style.display = 'none';
    return;
  }
  els.seasonDatesSection.style.display = 'block';

  // Show/hide drying off based on herd type
  els.dryingOffRow.style.display = herdType === 'Beef' ? 'none' : '';

  // Apply defaults if dropdowns are still empty
  const defaults = SEASON_DEFAULTS[herdType] || SEASON_DEFAULTS.Dairy;
  applySeasonDefaults(defaults);
}

function applySeasonDefaults(defaults) {
  const els = getElements();
  const selects = { calving: els.selCalving, turnout: els.selTurnout,
    housing: els.selHousing, breeding: els.selBreeding, dryingOff: els.selDryingOff };

  for (const [key, sel] of Object.entries(selects)) {
    const val = defaults[key];
    // Only set default if user hasn't already chosen
    if (!store.pendingSeasons[key] && val) {
      sel.value = String(val);
      store.pendingSeasons[key] = val;
    } else if (store.pendingSeasons[key]) {
      sel.value = String(store.pendingSeasons[key]);
    }
  }
}

export function updateChoiceButtons(btns, value) {
  const val = (value || '').toLowerCase();
  btns.forEach(btn => {
    const bVal = (btn.dataset.herd || '').toLowerCase();
    btn.classList.toggle('choice-btn-active', bVal === val);
  });
}

export function applyPendingProfileToUI() {
  const els = getElements();
  updateChoiceButtons(els.herdButtons, store.pendingProfile.herdType);

  if (store.pendingProfile.herdType) {
    showSeasonDates(store.pendingProfile.herdType);
  }
}

function readSeasonsFromUI() {
  const els = getElements();
  const seasons = {};
  seasons.calving = parseInt(els.selCalving.value) || null;
  seasons.turnout = parseInt(els.selTurnout.value) || null;
  seasons.housing = parseInt(els.selHousing.value) || null;
  seasons.breeding = parseInt(els.selBreeding.value) || null;
  seasons.dryingOff = store.pendingProfile.herdType === 'Beef'
    ? null
    : (parseInt(els.selDryingOff.value) || null);
  return seasons;
}

export function saveProfileAndContinue(renderCards) {
  const els = getElements();
  const seasons = readSeasonsFromUI();

  // Validate: herd type required + at least calving month
  if (!store.pendingProfile.herdType || !seasons.calving) {
    els.onboardError.style.display = 'inline';
    return false;
  }
  els.onboardError.style.display = 'none';

  // Save profile (herdType only)
  store.farmProfile = { ...store.pendingProfile };
  setItem(PROFILE_KEY, store.farmProfile);

  // Save seasons
  store.farmSeasons = seasons;
  store.pendingSeasons = { ...seasons };
  setItem(SEASONS_KEY, store.farmSeasons);

  toggleOnboarding(false);
  renderCards();
  return true;
}
